/**
 * Inline formatting model for a single block's text.
 *
 * The live editing buffer is *plain* text — no markdown tokens ever appear
 * in it — with formatting tracked separately as a list of (start, end, kind)
 * ranges ("marks"). Marks only turn into `<b>`/`<i>`/… tokens at the edges —
 * serializing for persistence, and parsing whatever markdown was persisted
 * before.
 *
 * Every `$` is KaTeX math: math spans ($..$, $$..$$, \(..\), \[..\]) are
 * copied verbatim on parse and serialize. Legacy <eq>…</eq> / <eqb>…</eqb>
 * (written by teacher-mobile's old editor, with every backslash doubled) is
 * converted to $…$ / $$…$$ on read by legacyEqToMath and never written.
 * Only backslashes immediately preceding escape-trigger characters are
 * escaped; LaTeX commands like `\frac` are never escaped.
 *
 * SHARED SOURCE: copied byte-for-byte into teacher-mobile and student-mobile
 * by scripts/sync-rich-content.js — edit here, then run that script.
 */

const KIND_ORDER = ['bold', 'italic', 'underline', 'strike', 'code', 'color', 'bg'];

function tokenFor(kind, param) {
  switch (kind) {
    case 'bold': return ['<b>', '</b>'];
    case 'italic': return ['<i>', '</i>'];
    case 'underline': return ['<u>', '</u>'];
    case 'strike': return ['<s>', '</s>'];
    case 'code': return ['<code>', '</code>'];
    case 'color': return [`<c:${param}>`, '</c>'];
    case 'bg': return [`<hl:${param}>`, '</hl>'];
    default: throw new Error(`unknown mark kind: ${kind}`);
  }
}

function markKey(m) { return `${m.kind}:${m.param ?? ''}`; }

// ── Parsing/serializing rules ───────────────────────────────────────────────
// Lesson content is mostly AI-written markdown full of KaTeX math, so the
// rules aim at "don't rewrite what the teacher didn't touch":
//  - math spans ($..$, $$..$$, \(..\), \[..\]) and `\$` are copied verbatim —
//    no marker parsing and no escaping inside them;
//  - `*`, `**`, `~~` and `` ` `` only act as markers when they pair up like
//    markdown emphasis (non-space inside, closer exists), so `l * w` stays text;
//  - serialization emits text verbatim whenever that re-parses identically,
//    escaping only as a fallback.

const EDITOR_TAG_AT = /^(?:<\/?(?:b|i|u|s|code|eqb?)>|<(?:c|hl):[a-z]+>|<\/(?:c|hl)>)/;

// Legacy <eq> content: undo the old mobile escapeInline, which prefixed
// every one of \ * ` ~ $ < with a backslash (so `\sum` was stored `\\sum`).
const LEGACY_ESCAPED_PAIR = /\\([\\*`~$<])/g;
const EDITOR_TAGS_ANYWHERE = /<\/?(?:b|i|u|s|code)>|<(?:c|hl):[a-z]+>|<\/(?:c|hl)>/g;

export function unescapeLegacyMath(inner) {
  return inner.replace(LEGACY_ESCAPED_PAIR, '$1');
}

/** Rewrites legacy <eq>X</eq> / <eqb>X</eqb> as $X$ / $$X$$. Escaped pairs
 * (`\<`) are skipped so a literal "<eq>" typed as text stays text; an
 * unclosed tag stays literal; an empty one is dropped. */
export function legacyEqToMath(raw) {
  if (!raw || raw.indexOf('<eq') === -1) return raw;
  let out = '';
  let i = 0;
  while (i < raw.length) {
    if (raw[i] === '\\' && i + 1 < raw.length) { out += raw[i] + raw[i + 1]; i += 2; continue; }
    const tag = raw.startsWith('<eqb>', i) ? 'eqb' : (raw.startsWith('<eq>', i) ? 'eq' : null);
    const close = tag ? raw.indexOf(`</${tag}>`, i + tag.length + 2) : -1;
    if (close === -1) { out += raw[i]; i += 1; continue; }
    const inner = unescapeLegacyMath(raw.slice(i + tag.length + 2, close).replace(EDITOR_TAGS_ANYWHERE, ''));
    const fence = tag === 'eqb' ? '$$' : '$';
    if (inner.trim()) out += `${fence}${inner}${fence}`;
    i = close + tag.length + 3;
  }
  return out;
}

function isBlank(c) { return c === undefined || /\s/.test(c); }

function findClosing(s, from, token) {
  let j = s.indexOf(token, from);
  while (j !== -1) {
    if (s[j - 1] !== '\\') return j;
    j = s.indexOf(token, j + 1);
  }
  return -1;
}

/** End index (exclusive) of the math span starting at `i`, or -1. */
function mathSpanEnd(s, i) {
  if (s[i] === '\\' && (s[i + 1] === '(' || s[i + 1] === '[')) {
    const close = s[i + 1] === '(' ? '\\)' : '\\]';
    const j = s.indexOf(close, i + 2);
    return j === -1 ? -1 : j + 2;
  }
  if (s[i] !== '$' || s[i - 1] === '\\') return -1;
  if (s[i + 1] === '$') {
    const j = findClosing(s, i + 2, '$$');
    return j === -1 ? -1 : j + 2;
  }
  const j = findClosing(s, i + 1, '$');
  return j > i + 1 ? j + 1 : -1;
}

/** Can `token` at raw[i] open a mark — non-blank right after it and a
 * plausible closer (non-blank before it) further along? */
function canOpenMarker(raw, i, token) {
  const after = i + token.length;
  if (isBlank(raw[after])) return false;
  let j = raw.indexOf(token, after + 1);
  while (j !== -1) {
    if (!isBlank(raw[j - 1]) && raw[j - 1] !== '\\') return true;
    j = raw.indexOf(token, j + 1);
  }
  return false;
}

function webMarkerAt(rest) {
  if (rest.startsWith('**')) return { token: '**', kind: 'bold' };
  if (rest.startsWith('~~')) return { token: '~~', kind: 'strike' };
  if (rest[0] === '`') return { token: '`', kind: 'code' };
  if (rest[0] === '*') return { token: '*', kind: 'italic' };
  return null;
}

function mathMask(text) {
  const mask = new Array(text.length).fill(false);
  let i = 0;
  while (i < text.length) {
    const end = mathSpanEnd(text, i);
    if (end === -1) { i += 1; continue; }
    for (let k = i; k < end; k++) mask[k] = true;
    i = end;
  }
  return mask;
}

function escapeWebRange(text, start, end, mask) {
  let out = '';
  for (let i = start; i < end; i++) {
    const c = text[i];
    if (mask[i]) { out += c; continue; }
    if (c === '\\') { out += /[\\*`~<]/.test(text[i + 1] ?? '') ? '\\\\' : '\\'; continue; }
    if (c === '*' || c === '`' || c === '~') { out += '\\' + c; continue; }
    if (c === '<' && EDITOR_TAG_AT.test(text.slice(i))) { out += '\\<'; continue; }
    out += c;
  }
  return out;
}

function sameInline(a, b) {
  const key = (ms) => ms.map((m) => `${m.kind}:${m.param ?? ''}:${m.start}:${m.end}`).join('|');
  return a.text === b.text && key(a.marks) === key(b.marks);
}

/** Plain text + (possibly overlapping) format ranges -> a markdown string
 * with properly nested tokens, closing and reopening at crossing
 * boundaries. Literal marker-like characters in the plain text are
 * backslash-escaped so they can't be mistaken for real tokens on reload
 * (only when emitting them verbatim would not reload identically). */
export function serializeInline(text, marks) {
  const verbatim = buildInline(text, marks, (s, e) => text.slice(s, e));
  if (sameInline(parseInline(verbatim), { text, marks: normalizeMarks(marks) })) return verbatim;
  const mask = mathMask(text);
  return buildInline(text, marks, (s, e) => escapeWebRange(text, s, e, mask));
}

function buildInline(text, marks, emitText) {
  if (!marks.length) return emitText(0, text.length);

  const cuts = new Set([0, text.length]);
  marks.forEach((m) => { cuts.add(m.start); cuts.add(m.end); });
  const points = Array.from(cuts).sort((a, b) => a - b);

  let out = '';
  let openStack = [];

  for (let i = 0; i < points.length - 1; i++) {
    const segStart = points[i];
    const segEnd = points[i + 1];
    if (segStart >= segEnd) continue;
    const active = marks
      .filter((m) => m.start <= segStart && m.end >= segEnd)
      .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));

    let common = 0;
    while (common < openStack.length && common < active.length && markKey(openStack[common]) === markKey(active[common])) common++;
    for (let k = openStack.length - 1; k >= common; k--) out += tokenFor(openStack[k].kind, openStack[k].param)[1];
    openStack = openStack.slice(0, common);
    for (let k = common; k < active.length; k++) {
      out += tokenFor(active[k].kind, active[k].param)[0];
      openStack.push(active[k]);
    }
    out += emitText(segStart, segEnd);
  }
  for (let k = openStack.length - 1; k >= 0; k--) out += tokenFor(openStack[k].kind, openStack[k].param)[1];
  return out;
}

/**
 * Reverse of serializeInline: markdown -> plain text + format ranges.
 * Legacy <eq>/<eqb> is first rewritten to $…$/$$…$$ (legacyEqToMath); math
 * spans and `\$` are then copied verbatim, so `$` never acts as a marker.
 */
export function parseInline(source) {
  const raw = legacyEqToMath(source ?? '');
  let text = '';
  const marks = [];
  const active = {};

  const open = (kind, param) => { active[kind] = { start: text.length, param }; };
  const close = (kind) => {
    const a = active[kind];
    if (!a) return;
    if (text.length > a.start) marks.push({ start: a.start, end: text.length, kind, param: a.param });
    delete active[kind];
  };
  const toggle = (kind) => { if (active[kind]) close(kind); else open(kind); };

  let i = 0;
  while (i < raw.length) {
    if (raw[i] === '\\' && raw[i + 1] === '$') { text += '\\$'; i += 2; continue; }
    const mathEnd = mathSpanEnd(raw, i);
    if (mathEnd !== -1) { text += raw.slice(i, mathEnd); i = mathEnd; continue; }
    if (raw[i] === '\\' && i + 1 < raw.length && /[\\*`~<]/.test(raw[i + 1])) {
      text += raw[i + 1];
      i += 2;
      continue;
    }
    const rest = raw.slice(i);
    if (rest.startsWith('</b>')) { close('bold'); i += 4; continue; }
    if (rest.startsWith('<b>')) { open('bold'); i += 3; continue; }
    if (rest.startsWith('</i>')) { close('italic'); i += 4; continue; }
    if (rest.startsWith('<i>')) { open('italic'); i += 3; continue; }
    if (rest.startsWith('</u>')) { close('underline'); i += 4; continue; }
    if (rest.startsWith('<u>')) { open('underline'); i += 3; continue; }
    if (rest.startsWith('</s>')) { close('strike'); i += 4; continue; }
    if (rest.startsWith('<s>')) { open('strike'); i += 3; continue; }
    if (rest.startsWith('</code>')) { close('code'); i += 7; continue; }
    if (rest.startsWith('<code>')) { open('code'); i += 6; continue; }
    if (rest.startsWith('</c>')) { close('color'); i += 4; continue; }
    if (rest.startsWith('</hl>')) { close('bg'); i += 5; continue; }
    const cOpen = rest.match(/^<c:([a-z]+)>/);
    if (cOpen) { open('color', cOpen[1]); i += cOpen[0].length; continue; }
    const hlOpen = rest.match(/^<hl:([a-z]+)>/);
    if (hlOpen) { open('bg', hlOpen[1]); i += hlOpen[0].length; continue; }
    // Bare markdown emphasis (AI-written content): only acts as a marker
    // when it pairs up like markdown, so `l * w` stays text.
    const fence = rest.match(/^`{3,}/);
    if (fence) { text += fence[0]; i += fence[0].length; continue; }
    const marker = webMarkerAt(rest);
    if (marker) {
      const { token, kind } = marker;
      const acts = active[kind] ? !isBlank(raw[i - 1]) : canOpenMarker(raw, i, token);
      if (acts) toggle(kind); else text += token;
      i += token.length;
      continue;
    }
    text += raw[i];
    i += 1;
  }
  Object.keys(active).forEach(close);
  return { text, marks: normalizeMarks(marks) };
}

/** Merges overlapping/touching marks that share a kind (+ param). */
export function normalizeMarks(marks) {
  const byKey = new Map();
  marks.forEach((m) => {
    if (m.start >= m.end) return;
    const k = markKey(m);
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(m);
  });
  const out = [];
  byKey.forEach((list) => {
    list.sort((a, b) => a.start - b.start);
    let cur = null;
    for (const m of list) {
      if (cur && m.start <= cur.end) cur.end = Math.max(cur.end, m.end);
      else { if (cur) out.push(cur); cur = { ...m }; }
    }
    if (cur) out.push(cur);
  });
  return out.sort((a, b) => a.start - b.start);
}

function isFullyCovered(marks, kind, start, end, param) {
  const relevant = marks
    .filter((m) => m.kind === kind && (param === undefined || m.param === param) && m.end > start && m.start < end)
    .sort((a, b) => a.start - b.start);
  let cursor = start;
  for (const m of relevant) {
    if (m.start > cursor) return false;
    cursor = Math.max(cursor, m.end);
    if (cursor >= end) return true;
  }
  return cursor >= end;
}

/** Clips/removes any marks of `kind` overlapping [start,end). */
export function removeMarkRange(marks, kind, start, end) {
  if (start >= end) return marks;
  const out = [];
  for (const m of marks) {
    if (m.kind !== kind || m.end <= start || m.start >= end) { out.push(m); continue; }
    if (m.start < start) out.push({ ...m, end: start });
    if (m.end > end) out.push({ ...m, start: end });
  }
  return normalizeMarks(out);
}

/** Toggles a mark over [start,end): removes it if the range is already
 * fully covered (matching param, for color/bg), otherwise clips any
 * competing same-kind marks in the range and adds the new one. */
export function toggleMark(marks, kind, start, end, param) {
  if (start >= end) return marks;
  const covered = isFullyCovered(marks, kind, start, end, param);
  const clipped = removeMarkRange(marks, kind, start, end);
  if (covered) return clipped;
  return normalizeMarks([...clipped, { kind, start, end, param }]);
}

/** Repositions marks after a text edit: `removed` characters at `at` were
 * replaced with `inserted` characters. */
export function rebaseMarks(marks, at, removed, inserted) {
  const map = (p) => {
    if (p < at) return p;
    if (p >= at + removed) return p - removed + inserted;
    return at + inserted;
  };
  const out = [];
  for (const m of marks) {
    const start = map(m.start);
    const end = map(m.end);
    if (start < end) out.push({ ...m, start, end });
  }
  return normalizeMarks(out);
}

/** Extracts the marks within [start,end), shifted relative to `start` —
 * used when a block is split into two. */
export function sliceMarks(marks, start, end) {
  const out = [];
  for (const m of marks) {
    const s = Math.max(m.start, start);
    const e = Math.min(m.end, end);
    if (s < e) out.push({ ...m, start: s - start, end: e - start });
  }
  return out;
}

/** Smallest (at, removed, inserted) edit that explains oldText -> newText
 * via common-prefix/common-suffix. */
export function diffText(oldText, newText) {
  const maxStart = Math.min(oldText.length, newText.length);
  let start = 0;
  while (start < maxStart && oldText[start] === newText[start]) start++;
  let oldEnd = oldText.length;
  let newEnd = newText.length;
  while (oldEnd > start && newEnd > start && oldText[oldEnd - 1] === newText[newEnd - 1]) { oldEnd--; newEnd--; }
  return { at: start, removed: oldEnd - start, inserted: newEnd - start };
}

// ── Math toggle (toolbar Σ) ─────────────────────────────────────────────────
// Math is stored as literal $…$ text, so Σ edits the text itself (inserting
// or removing the two delimiters) rather than toggling a mark. Formatting
// marks are kept atomic around a math span: a mark either covers the whole
// span or none of it, so serialization can never put a tag between `$`s.

const isDelimiter = (text, i) => text[i] === '$' && text[i - 1] !== '\\';
const hasUnescapedDollar = (text, start, end) => {
  for (let i = start; i < end; i++) if (isDelimiter(text, i)) return true;
  return false;
};

function remapMarks(marks, map) {
  return normalizeMarks(marks.map((m) => ({ ...m, start: map(m.start), end: map(m.end) })));
}

function snapMarksAround(marks, s, e) {
  const out = [];
  for (const m of marks) {
    if (m.end <= s || m.start >= e || (m.start <= s && m.end >= e)) { out.push(m); continue; }
    if (m.start < s) out.push({ ...m, end: s });
    if (m.end > e) out.push({ ...m, start: e });
  }
  return normalizeMarks(out);
}

function unwrapMath(text, marks, open, close, sel) {
  const next = text.slice(0, open) + text.slice(open + 1, close) + text.slice(close + 1);
  const map = (p) => (p <= open ? p : (p <= close ? p - 1 : p - 2));
  return { text: next, marks: remapMarks(marks, map), sel };
}

/** Wraps the selection in $…$, or removes the $…$ around/at the edges of it.
 * Does nothing for an empty selection, inside $$…$$ block math, or when the
 * selection would otherwise cut through an existing math span. */
export function toggleMath(text, marks, sel) {
  const start = Math.min(sel.start, sel.end);
  const end = Math.max(sel.start, sel.end);
  const unchanged = { text, marks, sel: { start, end } };
  if (start === end) return unchanged;

  const innerWrapped = end - start >= 2 && isDelimiter(text, start) && isDelimiter(text, end - 1);
  if (innerWrapped) {
    if (text[start + 1] === '$' || text[end - 2] === '$') return unchanged;
    if (hasUnescapedDollar(text, start + 1, end - 1)) return unchanged;
    return unwrapMath(text, marks, start, end - 1, { start, end: end - 2 });
  }
  const outerWrapped = start > 0 && isDelimiter(text, start - 1) && isDelimiter(text, end);
  if (outerWrapped) {
    if (text[start - 2] === '$' || text[end + 1] === '$') return unchanged;
    if (hasUnescapedDollar(text, start, end)) return unchanged;
    return unwrapMath(text, marks, start - 1, end, { start: start - 1, end: end - 1 });
  }
  if (hasUnescapedDollar(text, start, end)) return unchanged;
  // A selection inside an existing math span ($…$, $$…$$, \(…\), \[…\])
  // would split it into two broken spans.
  const mask = mathMask(text);
  for (let i = start; i < end; i++) if (mask[i]) return unchanged;

  const next = `${text.slice(0, start)}$${text.slice(start, end)}$${text.slice(end)}`;
  const map = (p) => (p <= start ? p : (p < end ? p + 1 : p + 2));
  return {
    text: next,
    marks: snapMarksAround(remapMarks(marks, map), start, end + 2),
    sel: { start: start + 1, end: end + 1 },
  };
}
